import { fmtINR } from '../../lib/currency';
import { useCallback, useEffect, useState } from 'react';
import {
  demandPlanningApi,
  type ActivityEntry,
  type DemandAdjustment,
  type DemandApproval,
  type DemandAssumption,
  type DemandCalendar,
  type DemandDashboard,
  type DemandKPI,
  type DemandPlan,
  type DemandScenario,
  type DemandTask,
  type DemandVersion,
  type HierarchyNode,
  type ScenarioComparison,
  type VarianceReport,
} from '../../api/demandplanning';

type Tab = 'overview' | 'hierarchy' | 'scenarios' | 'adjustments' | 'approvals' | 'variance' | 'activity';
type Theme = 'light' | 'dark';

function statusClass(status: string, dark: boolean) {
  const base = dark ? 'dark:' : '';
  if (status === 'approved' || status === 'published' || status === 'locked')
    return dark ? 'text-emerald-300 bg-emerald-950' : 'text-emerald-700 bg-emerald-50';
  if (status === 'pending' || status === 'submitted' || status === 'review')
    return dark ? 'text-amber-300 bg-amber-950' : 'text-amber-700 bg-amber-50';
  if (status === 'draft' || status === 'working')
    return dark ? 'text-slate-300 bg-slate-800' : 'text-slate-600 bg-slate-50';
  return dark ? 'text-blue-300 bg-blue-950' : 'text-blue-700 bg-blue-50';
}


function fmtNum(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function HierarchyTree({ nodes, depth = 0, dark }: { nodes: HierarchyNode[]; depth?: number; dark: boolean }) {
  return (
    <ul className={depth === 0 ? 'space-y-1' : 'ml-4 mt-1 space-y-1 border-l border-slate-200 pl-3 dark:border-slate-700'}>
      {nodes.map((n) => (
        <li key={n.id}>
          <div className={`flex items-center justify-between rounded px-2 py-1 text-sm ${dark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
            <span>
              <span className="text-xs uppercase text-slate-400">{n.level}</span>{' '}
              <span className="font-medium">{n.label}</span>
            </span>
            <span className="text-slate-500">{fmtNum(n.forecast_units)} units · {fmtINR(n.forecast_revenue)}</span>
          </div>
          {n.children && n.children.length > 0 && <HierarchyTree nodes={n.children} depth={depth + 1} dark={dark} />}
        </li>
      ))}
    </ul>
  );
}

export function DemandPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<DemandDashboard | null>(null);
  const [plan, setPlan] = useState<DemandPlan | null>(null);
  const [versions, setVersions] = useState<DemandVersion[]>([]);
  const [scenarios, setScenarios] = useState<DemandScenario[]>([]);
  const [comparison, setComparison] = useState<ScenarioComparison | null>(null);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [variance, setVariance] = useState<VarianceReport | null>(null);
  const [adjustments, setAdjustments] = useState<DemandAdjustment[]>([]);
  const [assumptions, setAssumptions] = useState<DemandAssumption[]>([]);
  const [kpis, setKpis] = useState<DemandKPI[]>([]);
  const [approvals, setApprovals] = useState<DemandApproval[]>([]);
  const [tasks, setTasks] = useState<DemandTask[]>([]);
  const [calendar, setCalendar] = useState<DemandCalendar | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans, appr, act] = await Promise.all([
        demandPlanningApi.dashboard(),
        demandPlanningApi.listPlans(),
        demandPlanningApi.listApprovals(),
        demandPlanningApi.listActivity(),
      ]);
      setDashboard(dash);
      setApprovals(appr);
      setActivity(act);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;

      const [ver, sc, asm, kpiList, tsk, cal] = await Promise.all([
        demandPlanningApi.listVersions(p.id),
        demandPlanningApi.listScenarios(p.id),
        demandPlanningApi.listAssumptions(p.id),
        demandPlanningApi.listKPIs(p.id),
        demandPlanningApi.listTasks(p.id),
        demandPlanningApi.getCalendar(p.id).catch(() => null),
      ]);
      setVersions(ver);
      setScenarios(sc);
      setAssumptions(asm);
      setKpis(kpiList);
      setTasks(tsk);
      setCalendar(cal);

      const working = ver.find((v) => v.status === 'working') ?? ver[0];
      if (working) {
        const [hier, adj, cmp, varR] = await Promise.all([
          demandPlanningApi.getHierarchy(working.id),
          demandPlanningApi.listAdjustments(working.id),
          demandPlanningApi.compareScenarios(p.id),
          demandPlanningApi.getVariance(p.id, working.id),
        ]);
        setHierarchy(hier);
        setAdjustments(adj);
        setComparison(cmp);
        setVariance(varR);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load demand planning');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleApprove = async (id: string) => {
    setBusy(true);
    try {
      await demandPlanningApi.decideApproval(id, true);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Dashboard' },
    { id: 'hierarchy', label: 'Hierarchy' },
    { id: 'scenarios', label: 'Scenarios' },
    { id: 'adjustments', label: 'Adjustments' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'variance', label: 'Variance' },
    { id: 'activity', label: 'Activity' },
  ];

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 shadow-sm' : 'rounded-xl border border-slate-200 bg-white shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Demand Planning</h1>
            <p className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
              Authoritative demand plans — collaborative forecasting, hierarchy rollup, scenarios & approvals
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTheme(dark ? 'light' : 'dark')}
              className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
              aria-label="Toggle dark mode"
            >
              {dark ? 'Light' : 'Dark'}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
        {plan && (
          <p className={`mt-2 text-sm ${dark ? 'text-slate-500' : 'text-slate-500'}`}>
            Plan: <span className="font-medium">{plan.name}</span>
            {' · '}{plan.horizon} · FY{plan.fiscal_year}
            {calendar && ` · ${calendar.name}`}
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Demand planning sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${
                tab === t.id ? 'border-indigo-600 text-indigo-600' : `border-transparent ${dark ? 'text-slate-400' : 'text-slate-600'}`
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {tab === 'overview' && dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
              {[
                { label: 'Forecast Units', value: fmtNum(dashboard.total_forecast_units) },
                { label: 'Forecast Revenue', value: fmtINR(dashboard.total_forecast_revenue) },
                { label: 'Growth %', value: `${dashboard.avg_growth_pct.toFixed(1)}%` },
                { label: 'Pending Approvals', value: dashboard.pending_approvals },
                { label: 'Variance Alerts', value: dashboard.variance_alerts },
                { label: 'Open Tasks', value: dashboard.open_tasks },
              ].map((k) => (
                <div key={k.label} className={`${card} p-4`}>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{k.label}</p>
                  <p className="mt-1 text-xl font-semibold">{k.value}</p>
                </div>
              ))}
            </div>

            {kpis.length > 0 && (
              <div className={`${card} p-6`}>
                <h2 className="text-lg font-semibold">Demand KPIs</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Forecast accuracy & bias are placeholders until AI forecast engine (DEV-000066)
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-slate-500">Top Opportunity</p>
                    <p className="font-medium">{kpis[0].top_opportunity ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Top Risk</p>
                    <p className="font-medium">{kpis[0].top_risk ?? '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {assumptions.length > 0 && (
              <div className={`${card} p-6`}>
                <h2 className="text-lg font-semibold">Demand Assumptions</h2>
                <ul className="mt-4 space-y-3">
                  {assumptions.map((a) => (
                    <li key={a.id} className={`rounded-lg px-4 py-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                      <span className="text-xs uppercase text-slate-500">{a.category}</span>
                      <p>{a.statement}</p>
                      {a.value && <p className="font-medium text-indigo-500">{a.value}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tasks.length > 0 && (
              <div className={`${card} p-6`}>
                <h2 className="text-lg font-semibold">Open Tasks</h2>
                <ul className="mt-3 space-y-2">
                  {tasks.map((t) => (
                    <li key={t.id} className="flex justify-between text-sm">
                      <span>{t.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(t.status, dark)}`}>{t.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === 'hierarchy' && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Planning Hierarchy</h2>
            <p className="mt-1 text-sm text-slate-500">Enterprise → Category → Brand → SKU with automatic rollup</p>
            <div className="mt-4">
              {hierarchy.length > 0 ? <HierarchyTree nodes={hierarchy} dark={dark} /> : <p className="text-sm text-slate-500">No hierarchy data</p>}
            </div>
          </div>
        )}

        {tab === 'scenarios' && comparison && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Scenario Comparison</h2>
            <table className="mt-4 min-w-full text-sm">
              <thead>
                <tr className={`border-b ${dark ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                  <th className="pb-2 text-left">Scenario</th>
                  <th className="pb-2 text-left">Units</th>
                  <th className="pb-2 text-left">Revenue</th>
                  <th className="pb-2 text-left">Variance</th>
                </tr>
              </thead>
              <tbody>
                {comparison.scenarios.map((sc) => {
                  const k = comparison.by_scenario[sc.id];
                  const v = comparison.variance_pct[sc.id];
                  return (
                    <tr key={sc.id} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                      <td className="py-2 font-medium">{sc.name}</td>
                      <td className="py-2">{k ? fmtNum(k.forecast_units) : '—'}</td>
                      <td className="py-2">{k ? fmtINR(k.forecast_revenue) : '—'}</td>
                      <td className="py-2">{v !== undefined ? `${v.toFixed(1)}%` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'adjustments' && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Demand Adjustments</h2>
            <p className="text-sm text-slate-500">Every adjustment is auditable with reason codes</p>
            <ul className="mt-4 space-y-3">
              {adjustments.map((a) => (
                <li key={a.id} className={`rounded-lg px-4 py-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <div className="flex justify-between">
                    <span className="font-medium">{a.type.replace('_', ' ')}</span>
                    <span className="text-xs text-slate-500">{a.reason_code}</span>
                  </div>
                  <p className="text-sm">{a.description ?? `${a.previous_units} → ${a.new_units} units`}</p>
                </li>
              ))}
              {adjustments.length === 0 && <p className="text-sm text-slate-500">No adjustments recorded</p>}
            </ul>
          </div>
        )}

        {tab === 'approvals' && (
          <div className="space-y-3">
            {approvals.map((a) => (
              <div key={a.id} className={`${card} flex items-center justify-between p-4`}>
                <div>
                  <p className="font-medium">Version approval</p>
                  <p className="text-sm text-slate-500">{new Date(a.requested_at).toLocaleDateString()}</p>
                </div>
                {a.status === 'pending' ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleApprove(a.id)}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                ) : (
                  <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(a.status, dark)}`}>{a.status}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'variance' && variance && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Variance Dashboard</h2>
            <p className="text-sm text-slate-500">Total variance: {variance.total_variance_pct.toFixed(1)}%</p>
            <table className="mt-4 min-w-full text-sm">
              <thead>
                <tr className={`border-b ${dark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <th className="pb-2 text-left">Node</th>
                  <th className="pb-2 text-left">Baseline</th>
                  <th className="pb-2 text-left">Current</th>
                  <th className="pb-2 text-left">Variance</th>
                </tr>
              </thead>
              <tbody>
                {variance.items.map((item, i) => (
                  <tr key={i} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="py-2">{item.node_label}</td>
                    <td className="py-2">{fmtNum(item.baseline_units)}</td>
                    <td className="py-2">{fmtNum(item.current_units)}</td>
                    <td className={`py-2 ${Math.abs(item.variance_pct) > 10 ? 'text-amber-500' : ''}`}>
                      {item.variance_pct.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'activity' && (
          <div className="space-y-2">
            {activity.map((a) => (
              <div key={a.id} className={`${card} px-4 py-3 text-sm`}>
                <span className="font-medium">{a.action}</span> — {a.message}
                <span className="ml-2 text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        {versions.length > 0 && tab === 'overview' && (
          <div className={`${card} mt-6 p-6`}>
            <h2 className="text-lg font-semibold">Forecast Versions</h2>
            <div className="mt-3 space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between text-sm">
                  <span>{v.name} v{v.version}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(v.status, dark)}`}>{v.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
